import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { useState, useMemo, useEffect } from "react";
import React from "react";
import SwitchToggle from "../../../components/SwitchToggle";
import SearchBar from "../../../components/SearchBar";
import AddCategorySubcategory from "../../../Modals/addCategorySubcategory";
import { useGetCategories, useDeleteCategory, useUpdateCategory } from "../../../api/hooks/useCategory";
import { showSuccess, showError } from "../../../helpers/toast";
import { CATEGORY_TYPE } from "../../../api/types/category.types";
import type { Category } from "../../../api/types/category.types";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import Pagination from "../../../components/Panigation";

const ManageSubCategories = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState<Category | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // Items per page

  // Debounce search term (500ms delay)
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset to page 1 when search changes
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Get categoryId from navigation state
  const categoryId = location.state?.categoryId;
  const categoryName = location.state?.categoryName || "Category";

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedSubcategory(null);
  };

  // Fetch subcategories for the selected category - always call API, include search param only when searching
  const { data: subcategoriesData, isLoading: isLoadingSubcategories, refetch: refetchSubcategories, error: subcategoriesError } = useGetCategories(
    {
      parentId: categoryId, // Filter by parent category
      ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }), // Only include search if there's a search term
      page,
      limit,
      offset: (page - 1) * limit,
    },
    { enabled: !!categoryId } // Only enable query when categoryId exists
  );

  // Extract subcategories and totalCount from response
  const { subcategories, totalCount } = useMemo(() => {
    const data = subcategoriesData?.data;
    let subs: Category[] = [];
    let total = 0;
    
    if (Array.isArray(data)) {
      subs = data;
      total = data.length;
    } else if (data && typeof data === 'object') {
      const dataObj = data as any;
      if ('list' in dataObj && Array.isArray(dataObj.list)) {
        subs = dataObj.list;
        total = dataObj.totalCount || dataObj.list.length;
      } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
        subs = dataObj.data;
        total = dataObj.totalCount || dataObj.data.length;
      } else if (dataObj.totalCount !== undefined) {
        total = dataObj.totalCount;
      }
    }
    
    if (!Array.isArray(subs)) {
      subs = [];
    }
    
    const filtered = subs.filter((sub: Category) => sub && sub.type === CATEGORY_TYPE.SUB_CATEGORY);
    
    return {
      subcategories: filtered,
      totalCount: total || filtered.length,
    };
  }, [subcategoriesData]);

  // Debug: Log API response
  useEffect(() => {
    if (subcategoriesData) {
      console.log('Subcategories API Response:', subcategoriesData);
    }
    if (subcategoriesError) {
      console.error('Subcategories API Error:', subcategoriesError);
      showError((subcategoriesError as any)?.response?.data?.message || 'Failed to load subcategories');
    }
  }, [subcategoriesData, subcategoriesError]);

  // Use subcategories directly (no client-side filtering since we use server-side search)
  const filteredSubcategories = subcategories;

  // Delete subcategory mutation
  const { mutate: deleteSubcategory, isPending: isDeleting } = useDeleteCategory({
    onSuccess: (response) => {
      showSuccess(response.message || "Subcategory deleted successfully");
      refetchSubcategories();
      setDeleteDialogOpen(false);
      setSubcategoryToDelete(null);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to delete subcategory");
    },
  });

  // Update subcategory status mutation
  const { mutate: updateSubcategoryStatus, isPending: isUpdatingStatus } = useUpdateCategory({
    onSuccess: (response) => {
      showSuccess(response.message || "Status updated successfully");
      refetchSubcategories();
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update status");
    },
  });

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  // Handle edit subcategory
  const handleEditSubcategory = (subcategory: Category) => {
    setSelectedSubcategory(subcategory);
    setOpenModal(true);
  };

  // Handle delete subcategory
  const handleDeleteSubcategory = (id: string) => {
    setSubcategoryToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Confirm delete subcategory
  const handleConfirmDelete = () => {
    if (subcategoryToDelete) {
      deleteSubcategory(subcategoryToDelete);
    }
  };

  // Cancel delete subcategory
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSubcategoryToDelete(null);
  };

  // Handle add new subcategory
  const handleAddSubcategory = () => {
    if (!categoryId) {
      showError("Please select a category first");
      navigate("/manage-categories");
      return;
    }
    setSelectedSubcategory(null);
    setOpenModal(true);
  };

  // Handle status toggle
  const handleStatusToggle = (subcategory: Category) => (event: React.ChangeEvent<HTMLInputElement>) => {
    updateSubcategoryStatus({
      id: subcategory._id,
      data: {
        status: event.target.checked,
      },
    });
  };

  // Redirect if no categoryId
  if (!categoryId) {
    return (
      <div className="main_title">
        <h1>Manage Sub Categories</h1>
        <p>Please select a category first.</p>
        <Button onClick={() => navigate("/manage-categories")}>
          Go to Categories
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="main_title">
        <h1>Manage Sub Categories</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          <button onClick={() => navigate("/manage-categories")}>
            Manage Categories
          </button>{" "}
          - {categoryName} - Manage Sub Categories
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search Sub-Categories..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              onCross={() => {
                setSearchTerm("");
                setDebouncedSearchTerm("");
              }}
            />
          </div>

          <div className="right_s">
            <Button 
              className="btn btn_primary" 
              onClick={handleAddSubcategory}
              disabled={isLoadingSubcategories || isDeleting || isUpdatingStatus}
            >
              <AddIcon /> Add Sub-Category
            </Button>
          </div>
        </div>

        <TableContainer className="table_container">
          {isLoadingSubcategories ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
            </Box>
          ) : subcategoriesError ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <p>Error loading subcategories. Please try again.</p>
              <Button onClick={() => refetchSubcategories()} style={{ marginLeft: 10 }}>
                Retry
              </Button>
            </Box>
          ) : filteredSubcategories.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <p>
                {debouncedSearchTerm.trim() 
                  ? `No subcategories found for "${debouncedSearchTerm}"`
                  : "No subcategories found"}
              </p>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr. No.</TableCell>
                  <TableCell>Sub-Category Name</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredSubcategories.map((subcategory: Category, index: number) => (
                  <TableRow hover key={subcategory._id}>
                    <TableCell>{(page - 1) * limit + index + 1}</TableCell>

                    <TableCell>
                      <b>{subcategory.name}</b>
                    </TableCell>

                    <TableCell>{formatDate(subcategory.createdAt)}</TableCell>

                    <TableCell>
                      <SwitchToggle
                        checked={subcategory.status}
                        onChange={handleStatusToggle(subcategory)}
                        disabled={isLoadingSubcategories || isDeleting || isUpdatingStatus}
                      />
                    </TableCell>

                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton
                          onClick={() => handleEditSubcategory(subcategory)}
                          disabled={isLoadingSubcategories || isDeleting || isUpdatingStatus}
                          title="Edit Subcategory"
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>

                        <IconButton
                          onClick={() => handleDeleteSubcategory(subcategory._id)}
                          disabled={isLoadingSubcategories || isDeleting || isUpdatingStatus}
                          title="Delete Subcategory"
                        >
                          <img src="/static/images/trash2_icon.svg" alt="Delete" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        
        {/* Pagination */}
        {!isLoadingSubcategories && !subcategoriesError && totalCount > 0 && (
          <Pagination
            module={filteredSubcategories}
            page={page}
            onPageChange={(newPage) => {
              setPage(newPage);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            totalPages={Math.ceil(totalCount / limit)}
            totalCount={totalCount}
            setPage={setPage}
            limit={limit}
          />
        )}
      </div>

      <AddCategorySubcategory
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        category={selectedSubcategory}
        type={CATEGORY_TYPE.SUB_CATEGORY}
        parentId={categoryId}
        onSuccess={() => {
          refetchSubcategories();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Subcategory"
        message="Are you sure you want to delete this subcategory? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="error"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />
    </>
  );
};

export default ManageSubCategories;
